/** @jsx h */
import { component, h } from 'literaljs';
import { playBloop, playPop, playChime } from '../../utils/sounds';
import { todoLesson } from './data';

const PROGRESS_KEY = 'literaljs-tutorial-progress';
const COMPLETED_KEY = 'literaljs-tutorial-completed';

// Simple syntax highlighter for JSX/JS code
function highlightCode(code) {
	if (!code) return '';
	let escaped = code
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

	escaped = escaped.replace(/(\/\/.*$)/gm, '<span class="syn-comment">$1</span>');
	escaped = escaped.replace(/(&quot;.*?&quot;|&#039;.*?&#039;)/g, '<span class="syn-string">$1</span>');
	escaped = escaped.replace(/\b(const|let|var|function|return|if|else|for|while|import|export|from|default|new|this|true|false|null|undefined)\b/g, '<span class="syn-keyword">$1</span>');
	escaped = escaped.replace(/\b(\d+)\b/g, '<span class="syn-number">$1</span>');
	escaped = escaped.replace(/(&lt;\/?)([a-zA-Z][a-zA-Z0-9]*)/g, '$1<span class="syn-jsx-tag">$2</span>');
	escaped = escaped.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(?=\()/g, '<span class="syn-function">$1</span>');

	return escaped;
}

const LessonStep = component({
	name: 'LessonStep',
	props: ['step', 'isActive', 'isComplete', 'onClick'],
	render() {
		const { step, isActive, isComplete, onClick } = this.props;
		return (
			<div
				class={"lesson-step" +
					(isActive ? " -active" : "") +
					(isComplete ? " -complete" : "")
				}
				events={{ click: onClick }}
			>
				<div class="step-number">
					{isComplete ? (
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-label="Completed">
							<polyline points="20 6 9 17 4 12"></polyline>
						</svg>
					) : step.id}
				</div>
				<div class="step-info">
					<div class="step-title">{step.title}</div>
					<div class="step-desc">{step.description}</div>
				</div>
			</div>
		);
	}
});

const Celebration = component({
	name: 'Celebration',
	render() {
		const particles = Array.from({ length: 24 }, (_, i) => ({
			left: 10 + Math.random() * 80,
			delay: Math.random() * 2,
			duration: 2 + Math.random() * 2,
			hue: Math.floor(Math.random() * 60) + 240
		}));
		return (
			<div class="celebration-overlay" aria-hidden="true">
				{particles.map((p, i) => (
					<div
						key={i}
						class="celebration-particle"
						style={{
							left: p.left + '%',
							animationDelay: p.delay + 's',
							animationDuration: p.duration + 's',
							'--hue': p.hue
						}}
					></div>
				))}
			</div>
		);
	}
});

const AchievementBadge = component({
	name: 'AchievementBadge',
	props: ['title', 'description'],
	render() {
		const { title, description } = this.props;
		return (
			<div class="achievement-badge">
				<div class="achievement-icon" aria-hidden="true">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<circle cx="12" cy="8" r="6"></circle>
						<path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path>
					</svg>
				</div>
				<div class="achievement-info">
					<div class="achievement-title">{title}</div>
					<div class="achievement-desc">{description}</div>
				</div>
			</div>
		);
	}
});

export default component({
	name: 'TodoLesson',
	state: {
		currentStep: 0,
		completedSteps: [],
		showHint: false,
		showSolution: false,
		userCode: '',
		codeChecked: false,
		codeCorrect: false,
		showCelebration: false,
		showBadge: false,
		hintLevel: 0,
		codeOutput: null
	},
	mounted() {
		try {
			const savedProgress = localStorage.getItem(PROGRESS_KEY);
			const savedCompleted = localStorage.getItem(COMPLETED_KEY);
			if (savedProgress !== null) {
				const progress = parseInt(savedProgress, 10);
				if (!isNaN(progress) && progress >= 0 && progress < todoLesson.steps.length) {
					this.setState({ currentStep: progress });
				}
			}
			if (savedCompleted !== null) {
				const completed = JSON.parse(savedCompleted);
				if (Array.isArray(completed)) {
					this.setState({ completedSteps: completed });
				}
			}
		} catch (e) {
			console.warn('Failed to load tutorial progress:', e);
		}

		this._onKeydown = (e) => {
			const state = this.getState() || {};
			const { currentStep = 0, completedSteps = [] } = state;
			const maxStep = completedSteps.length;
			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
				e.preventDefault();
				if (currentStep < Math.min(maxStep + 1, todoLesson.steps.length - 1)) {
					this.goToStep(currentStep + 1);
				}
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
				e.preventDefault();
				if (currentStep > 0) {
					this.goToStep(currentStep - 1);
				}
			} else if (e.key === 'h' && !e.ctrlKey && !e.metaKey && !e.altKey) {
				if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'INPUT') {
					e.preventDefault();
					this.toggleHint();
				}
			}
		};
		document.addEventListener('keydown', this._onKeydown);
	},
	unmounted() {
		if (this._onKeydown) {
			document.removeEventListener('keydown', this._onKeydown);
		}
		if (this._advanceTimer) clearTimeout(this._advanceTimer);
		if (this._celebrationTimer) clearTimeout(this._celebrationTimer);
		if (this._badgeTimer) clearTimeout(this._badgeTimer);
	},
	methods() {
		return {
			goToStep(index) {
				if (index > this.getState().currentStep + 1) return;
				playBloop(0.9 + index * 0.1);
				this.setState({
					currentStep: index,
					showHint: false,
					showSolution: false,
					codeChecked: false,
					codeCorrect: false,
					hintLevel: 0,
					codeOutput: null
				});
				try {
					localStorage.setItem(PROGRESS_KEY, String(index));
				} catch (e) {
					console.warn('Failed to save progress:', e);
				}
			},
			toggleHint() {
				playPop();
				const { hintLevel } = this.getState();
				const step = todoLesson.steps[this.getState().currentStep];
				const maxHints = step.hints ? step.hints.length : 1;
				if (hintLevel < maxHints) {
					this.setState({ showHint: true, hintLevel: hintLevel + 1 });
				} else {
					this.setState({ showHint: false, hintLevel: 0 });
				}
			},
			updateCode(e) {
				this.setState({
					userCode: e.target.value,
					codeChecked: false,
					codeOutput: null
				});
			},
			_syncScroll() {
				const gutter = document.querySelector('.line-number-gutter');
				const textarea = document.getElementById('code-editor');
				if (gutter && textarea) {
					gutter.scrollTop = textarea.scrollTop;
				}
			},
			checkCode() {
				const { currentStep, userCode } = this.getState();
				const step = todoLesson.steps[currentStep];
				const isCorrect = step.check(userCode);

				if (isCorrect) {
					playChime();
					const completed = [...this.getState().completedSteps];
					if (!completed.includes(currentStep)) {
						completed.push(currentStep);
					}
					const allCompleted = completed.length === todoLesson.steps.length;

					this.setState({
						codeChecked: true,
						codeCorrect: true,
						completedSteps: completed,
						showCelebration: allCompleted,
						showBadge: allCompleted
					});

					try {
						localStorage.setItem(COMPLETED_KEY, JSON.stringify(completed));
					} catch (e) {
						console.warn('Failed to save completed steps:', e);
					}

					if (currentStep < todoLesson.steps.length - 1) {
						this._advanceTimer = setTimeout(() => {
							this.goToStep(currentStep + 1);
						}, 1500);
					}

					if (allCompleted) {
						this._celebrationTimer = setTimeout(() => {
							this.setState({ showCelebration: false });
						}, 5000);
						this._badgeTimer = setTimeout(() => {
							this.setState({ showBadge: false });
						}, 8000);
					}
				} else {
					playPop();
					this.setState({ codeChecked: true, codeCorrect: false });
				}
			},
			showSolution() {
				playPop();
				this.setState({ showSolution: true });
			},
			resetCode() {
				playPop();
				this.setState({ userCode: '', codeChecked: false, codeCorrect: false, codeOutput: null });
			},
			resetProgress() {
				playPop();
				this.setState({
					currentStep: 0,
					completedSteps: [],
					userCode: '',
					showCelebration: false,
					showBadge: false
				});
				try {
					localStorage.removeItem(PROGRESS_KEY);
					localStorage.removeItem(COMPLETED_KEY);
				} catch (e) {
					console.warn('Failed to clear progress:', e);
				}
			}
		};
	},
	render() {
		const { currentStep, completedSteps, showHint, showSolution, userCode, codeChecked, codeCorrect, showCelebration, showBadge, hintLevel, codeOutput } = this.getState();
		const step = todoLesson.steps[currentStep];
		const progress = ((completedSteps.length / todoLesson.steps.length) * 100).toFixed(0);
		const isLastStep = currentStep === todoLesson.steps.length - 1;
		const allCompleted = completedSteps.length === todoLesson.steps.length;

		const getHintText = () => {
			if (step.hints && hintLevel > 0) {
				return step.hints[Math.min(hintLevel - 1, step.hints.length - 1)];
			}
			return step.hint;
		};

		const highlightedSolution = highlightCode(step.code);

		return (
			<div class="todo-lesson">
				{showCelebration && <Celebration />}

				<div class="lesson-header">
					<div class="lesson-badge">Interactive Tutorial</div>
					<h2>{todoLesson.title}</h2>
					<p>{todoLesson.description}</p>
				</div>

				<div class="progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
					<div
						class="progress-fill"
						style={{ width: progress + '%' }}
					></div>
					<span class="progress-text">{progress}% Complete ({completedSteps.length}/{todoLesson.steps.length} steps)</span>
				</div>

				<div class="lesson-actions">
					<button
						class="reset-progress-btn"
						events={{ click: () => this.resetProgress() }}
						title="Reset all progress"
					>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
							<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
							<path d="M3 3v5h5"></path>
						</svg>
						Reset Progress
					</button>
				</div>

				<div class="lesson-layout">
					<div class="lesson-sidebar">
						{todoLesson.steps.map((s, i) => (
							<LessonStep
								key={s.id}
								step={s}
								isActive={i === currentStep}
								isComplete={completedSteps.includes(i)}
								onClick={() => this.goToStep(i)}
							/>
						))}
					</div>

					<div class="lesson-content">
						<div class="step-header">
							<div class="step-number-lg">Step {step.id}</div>
							<h3>{step.title}</h3>
						</div>

						<div class="task-card">
							<div class="task-label">Your Task</div>
							<p>{step.task}</p>
						</div>

						<div class="code-editor-container">
							<div class="editor-header">
								<span>Code Editor</span>
								<div class="editor-actions">
									<button
										class="hint-btn"
										aria-label={showHint ? 'Next hint' : 'Show hint'}
										events={{ click: () => this.toggleHint() }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
											<path d="M9 18h6"></path>
											<path d="M10 22h4"></path>
											<path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path>
										</svg>
										{showHint ? 'Next Hint' : 'Show Hint'}
										{step.hints && ` (${hintLevel}/${step.hints.length})`}
									</button>
									<button
										class="reset-btn"
										aria-label="Reset code"
										events={{ click: () => this.resetCode() }}
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
											<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
											<path d="M3 3v5h5"></path>
										</svg>
										Reset
									</button>
								</div>
							</div>

						{showHint && (
							<div class="hint-box">
								<span>{getHintText()}</span>
							</div>
						)}

						<div class="code-editor-wrapper">
							<div class="line-number-gutter">
								{userCode.split('\n').map((_, i) => (
									<div key={i}>{i + 1}</div>
								))}
								{userCode === '' && <div>1</div>}
							</div>
							<textarea
								id="code-editor"
								class="code-editor"
								placeholder="Type your code here..."
								aria-label="Code editor"
								events={{ input: (e) => this.updateCode(e), scroll: () => this._syncScroll() }}
							>{userCode}</textarea>
						</div>

							<div class="editor-footer">
								<button
									class="check-btn"
									aria-label="Check my code"
									events={{ click: () => this.checkCode() }}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
										<polyline points="20 6 9 17 4 12"></polyline>
									</svg>
									{codeCorrect ? 'Correct!' : 'Check My Code'}
								</button>
							<button
									class="solution-btn"
								events={{ click: () => this.showSolution() }}
							>
								Show Solution
							</button>
							</div>

						{codeChecked && !codeCorrect && (
							<div class="feedback -error">
								<span>Not quite! Check the hint and try again.</span>
							</div>
						)}
						</div>

						{showSolution && (
							<div class="solution-preview">
								<div class="solution-header">
									<span>Solution</span>
								</div>
								<pre class="syntax-highlighted"><code dangerouslySetInnerHTML={{ __html: highlightedSolution }} /></pre>
							</div>
						)}

						{showBadge && (
							<AchievementBadge
								title="LiteralJS Master"
								description={`Completed all ${todoLesson.steps.length} tutorial steps!`}
							/>
						)}

						{isLastStep && completedSteps.includes(currentStep) && (
							<div class="completion-celebration">
								<div class="celebration-icon" aria-hidden="true">
									<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
										<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
										<path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
										<path d="M4 22h16"></path>
										<path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
										<path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
										<path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
									</svg>
								</div>
								<h3>Congratulations!</h3>
								<p>You completed the Todo App tutorial.</p>
								<div class="celebration-stats">
									<span>{completedSteps.length} steps completed</span>
									<span>{progress}% progress</span>
								</div>
								{allCompleted && (
									<AchievementBadge
										title="LiteralJS Master"
										description={`Completed all ${todoLesson.steps.length} tutorial steps!`}
									/>
								)}
								<button
									class="restart-btn"
									events={{ click: () => this.resetProgress() }}
								>
									Start Over
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
});
